import React, { useState, useEffect } from "react";
import { auth, db } from "./firebase";
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  User,
} from "firebase/auth";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";

// Ant Design Components
import {
  Layout,
  Card,
  Form,
  Input,
  Button,
  Table,
  Tag,
  Space,
  Switch,
  Modal,
  Popconfirm,
  Statistic,
  Row,
  Col,
  Typography,
  message,
  Tooltip,
  ConfigProvider,
  Badge,
} from "antd";

// Ant Design Icons
import {
  KeyOutlined,
  GlobalOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  UserOutlined,
  LogoutOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  SafetyCertificateOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;

interface License {
  id: string;
  domain: string;
  licenseKey: string;
  isActive: boolean;
  createdAt?: any;
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Modal States
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState<License | null>(null);

  // Forms
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  // Auth Listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoadingUser(false);
      if (currentUser) {
        fetchLicenses();
      }
    });
    return () => unsubscribe();
  }, []);

  // ---------------------------------------------------------------------------
  // CRUD OPERATIONS
  // ---------------------------------------------------------------------------

  // READ: Fetch Licenses
  const fetchLicenses = async () => {
    setTableLoading(true);
    try {
      const q = query(collection(db, "licenses"), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const data: License[] = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() as Omit<License, "id">),
      }));
      setLicenses(data);
    } catch (error) {
      // Fallback query if index is building or missing
      try {
        const querySnapshot = await getDocs(collection(db, "licenses"));
        const data: License[] = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...(docSnap.data() as Omit<License, "id">),
        }));
        setLicenses(data);
      } catch (err) {
        message.error("Failed to load license records.");
      }
    } finally {
      setTableLoading(false);
    }
  };

  // CREATE: Generate New License
  const handleCreateLicense = async (values: { domain: string }) => {
    try {
      const newLicense = {
        licenseKey: uuidv4(),
        domain: values.domain.trim().toLowerCase(),
        isActive: true,
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, "licenses"), newLicense);
      message.success(`License successfully generated for ${values.domain}`);
      createForm.resetFields();
      setIsCreateModalOpen(false);
      fetchLicenses();
    } catch (error) {
      message.error("Failed to generate license key.");
    }
  };

  // UPDATE: Toggle Active Status
  const handleToggleStatus = async (license: License) => {
    try {
      const docRef = doc(db, "licenses", license.id);
      await updateDoc(docRef, { isActive: !license.isActive });
      message.success(
        `License ${!license.isActive ? "activated" : "deactivated"}`,
      );
      setLicenses((prev) =>
        prev.map((item) =>
          item.id === license.id ? { ...item, isActive: !item.isActive } : item,
        ),
      );
    } catch (error) {
      message.error("Failed to update status.");
    }
  };

  // UPDATE: Edit Domain
  const handleUpdateLicense = async (values: { domain: string }) => {
    if (!editingLicense) return;
    try {
      const docRef = doc(db, "licenses", editingLicense.id);
      await updateDoc(docRef, { domain: values.domain.trim().toLowerCase() });
      message.success("Domain updated successfully");
      setIsEditModalOpen(false);
      setEditingLicense(null);
      fetchLicenses();
    } catch (error) {
      message.error("Failed to update domain.");
    }
  };

  // DELETE: Remove License
  const handleDeleteLicense = async (id: string) => {
    try {
      await deleteDoc(doc(db, "licenses", id));
      message.success("License deleted permanently.");
      setLicenses((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      message.error("Failed to delete license.");
    }
  };

  // AUTH: Login
  const handleLogin = async (values: { email: string; password: string }) => {
    setLoginLoading(true);
    try {
      await signInWithEmailAndPassword(auth, values.email, values.password);
      message.success("Welcome back, Admin!");
    } catch (error) {
      message.error("Invalid credentials. Please check your email/password.");
    } finally {
      setLoginLoading(false);
    }
  };

  // AUTH: Logout
  const handleLogout = async () => {
    await signOut(auth);
    message.info("Signed out successfully.");
  };

  // ---------------------------------------------------------------------------
  // FILTERING & METRICS
  // ---------------------------------------------------------------------------
  const filteredLicenses = licenses.filter(
    (lic) =>
      lic.domain.toLowerCase().includes(searchText.toLowerCase()) ||
      lic.licenseKey.toLowerCase().includes(searchText.toLowerCase()),
  );

  const activeCount = licenses.filter((l) => l.isActive).length;
  const revokedCount = licenses.length - activeCount;

  // ---------------------------------------------------------------------------
  // TABLE COLUMNS
  // ---------------------------------------------------------------------------
  const columns = [
    {
      title: "Domain",
      dataIndex: "domain",
      key: "domain",
      render: (domain: string) => (
        <Space>
          <GlobalOutlined style={{ color: "#4f46e5" }} />
          <Text strong>{domain}</Text>
        </Space>
      ),
    },
    {
      title: "License Key",
      dataIndex: "licenseKey",
      key: "licenseKey",
      render: (key: string) => (
        <Space>
          <Paragraph copyable={{ text: key }} style={{ margin: 0 }}>
            <code
              style={{
                background: "#f1f5f9",
                padding: "2px 8px",
                borderRadius: "4px",
                fontSize: "13px",
                color: "#334155",
              }}
            >
              {key}
            </code>
          </Paragraph>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean, record: License) => (
        <Space size="middle">
          <Tag
            icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            color={isActive ? "success" : "error"}
            style={{ borderRadius: "12px", padding: "2px 10px" }}
          >
            {isActive ? "Active" : "Revoked"}
          </Tag>
          <Switch
            checked={isActive}
            size="small"
            onChange={() => handleToggleStatus(record)}
          />
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: License) => (
        <Space size="small">
          <Tooltip title="Edit Domain">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "#0284c7" }} />}
              onClick={() => {
                setEditingLicense(record);
                editForm.setFieldsValue({ domain: record.domain });
                setIsEditModalOpen(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this license?"
            description="Software using this key will no longer be authorized."
            onConfirm={() => handleDeleteLicense(record.id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete License">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ---------------------------------------------------------------------------
  // RENDER VIEWS
  // ---------------------------------------------------------------------------

  if (loadingUser) {
    return (
      <div
        style={{
          display: "flex",
          height: "100vh",
          justifyContent: "center",
          alignItems: "center",
          background: "#f8fafc",
        }}
      >
        <Badge status="processing" text="Loading License Portal..." />
      </div>
    );
  }

  // 1. LOGIN SCREEN
  if (!user) {
    return (
      <ConfigProvider theme={{ token: { colorPrimary: "#4f46e5" } }}>
        <div
          style={{
            minHeight: "100vh",
            background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
          }}
        >
          <Card
            style={{
              width: "100%",
              maxWidth: 420,
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.3)",
              borderRadius: "16px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "#e0e7ff",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 12,
                }}
              >
                <SafetyCertificateOutlined
                  style={{ fontSize: 28, color: "#4f46e5" }}
                />
              </div>
              <Title level={3} style={{ margin: 0 }}>
                License Manager
              </Title>
              <Text type="secondary">license.soft.io.vn</Text>
            </div>

            <Form layout="vertical" onFinish={handleLogin} size="large">
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: "Please input your admin email!" },
                  { type: "email", message: "Please enter a valid email!" },
                ]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: "#94a3b8" }} />}
                  placeholder="Admin Email"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: "Please input your password!" },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
                  placeholder="Password"
                />
              </Form.Item>

              <Form.Item style={{ marginTop: 24 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loginLoading}
                  style={{
                    background: "#4f46e5",
                    borderColor: "#4f46e5",
                    height: "44px",
                    borderRadius: "8px",
                    fontWeight: 600,
                  }}
                >
                  Sign In to Dashboard
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </div>
      </ConfigProvider>
    );
  }

  // 2. DASHBOARD
  return (
    <ConfigProvider theme={{ token: { colorPrimary: "#4f46e5" } }}>
      <Layout style={{ minHeight: "100vh", background: "#f8fafc" }}>
        {/* HEADER */}
        <Header
          style={{
            background: "#ffffff",
            padding: "0 32px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Space size="middle">
            <SafetyCertificateOutlined
              style={{ fontSize: 24, color: "#4f46e5" }}
            />
            <Title level={4} style={{ margin: 0 }}>
              License Portal
            </Title>
            <Tag color="purple">license.soft.io.vn</Tag>
          </Space>

          <Space>
            <Text type="secondary">{user.email}</Text>
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              danger
            >
              Logout
            </Button>
          </Space>
        </Header>

        {/* MAIN CONTENT */}
        <Content
          style={{
            padding: "32px",
            maxWidth: 1200,
            margin: "0 auto",
            width: "100%",
          }}
        >
          {/* STATS METRICS */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 12,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
              >
                <Statistic
                  title="Total Licenses"
                  value={licenses.length}
                  prefix={<KeyOutlined style={{ color: "#4f46e5" }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 12,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
              >
                <Statistic
                  title="Active Authorizations"
                  value={activeCount}
                  valueStyle={{ color: "#16a34a" }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 12,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
              >
                <Statistic
                  title="Revoked Licenses"
                  value={revokedCount}
                  valueStyle={{ color: "#dc2626" }}
                  prefix={<CloseCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* TABLE ACTIONS CARD */}
          <Card
            bordered={false}
            style={{
              borderRadius: 12,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px",
                marginBottom: 20,
              }}
            >
              <Input
                placeholder="Search domain or key..."
                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                style={{ width: 300, borderRadius: "8px" }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />

              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchLicenses}
                  loading={tableLoading}
                >
                  Refresh
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsCreateModalOpen(true)}
                  style={{
                    background: "#4f46e5",
                    borderColor: "#4f46e5",
                    borderRadius: "8px",
                  }}
                >
                  Generate License Key
                </Button>
              </Space>
            </div>

            {/* LICENSES TABLE */}
            <Table
              columns={columns}
              dataSource={filteredLicenses}
              rowKey="id"
              loading={tableLoading}
              pagination={{ pageSize: 8, hideOnSinglePage: true }}
            />
          </Card>
        </Content>

        {/* FOOTER */}
        <Footer style={{ textAlign: "center", color: "#94a3b8" }}>
          License Management System ©{new Date().getFullYear()} •
          license.soft.io.vn
        </Footer>

        {/* ------------------------------------------------------------------- */}
        {/* MODAL 1: CREATE LICENSE */}
        {/* ------------------------------------------------------------------- */}
        <Modal
          title="Generate New License"
          open={isCreateModalOpen}
          onCancel={() => setIsCreateModalOpen(false)}
          footer={null}
          destroyOnClose
        >
          <Form
            form={createForm}
            layout="vertical"
            onFinish={handleCreateLicense}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="domain"
              label="Authorized Domain Name"
              rules={[
                { required: true, message: "Please enter a valid domain" },
                {
                  pattern:
                    /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/,
                  message:
                    "Enter a valid domain name (e.g., mysite.com or plugin.co.uk)",
                },
              ]}
            >
              <Input
                prefix={<GlobalOutlined style={{ color: "#94a3b8" }} />}
                placeholder="e.g. clientdomain.com"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
              <Space>
                <Button onClick={() => setIsCreateModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  Generate Key
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* ------------------------------------------------------------------- */}
        {/* MODAL 2: EDIT DOMAIN */}
        {/* ------------------------------------------------------------------- */}
        <Modal
          title="Edit Authorized Domain"
          open={isEditModalOpen}
          onCancel={() => {
            setIsEditModalOpen(false);
            setEditingLicense(null);
          }}
          footer={null}
          destroyOnClose
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleUpdateLicense}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="domain"
              label="Domain Name"
              rules={[{ required: true, message: "Domain is required" }]}
            >
              <Input
                prefix={<GlobalOutlined style={{ color: "#94a3b8" }} />}
                placeholder="e.g. clientdomain.com"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
              <Space>
                <Button
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingLicense(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  Save Changes
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Layout>
    </ConfigProvider>
  );
}
